import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo, useEffect } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import ShopperTextEditor from 'components/TextEditor';

import useDialog from 'hooks/useDialog';
import usePendingComments from 'hooks/usePendingComments';
import useToast from 'hooks/useToast';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import { useDomain } from 'providers/DomainProvider';
import { useUser } from 'providers/UserProvider';

import { useCommentsRoot } from '../Root';
import UserAvatar from './UserAvatar';

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const TextEditor = ({
  aliasId,
  answering,
  children,
  className,
  commentId,
  editing = false,
  initialValue,
  parentId,
  onCancel,
  onSend,
}) => {
  const { showToast } = useToast();
  const { showDialog } = useDialog();
  const { domain, domainId, domainIdKey } = useDomain();
  const {
    createPendingComment,
    deletePendingCommentsFromDomain,
    getPendingCommentsFromDomain,
  } = usePendingComments();
  const { gaEventCategory, createComment, editComment } = useCommentsRoot();
  const { loadingLoggedInUser, isLoggedIn } = useUser();

  useEffect(() => {
    const createRecoveredComment = () => {
      if (!isLoggedIn) {
        return;
      }

      const pendingComments = getPendingCommentsFromDomain(domain, domainId);

      if (pendingComments.length === 0) {
        return;
      }

      pendingComments.map(({ comment }) => {
        createComment(comment);
      });
      deletePendingCommentsFromDomain(domain, domainId);
      showToast({
        text: placeholder('toasts.successes.comments.created'),
        type: 'success',
      });
    };

    createRecoveredComment();
  }, [isLoggedIn]);

  const onFocus = () => {
    sendEvent({
      action: isLoggedIn ? 'textbox_click_logged' : 'textbox_click_unlogged',
      category: gaEventCategory,
    });
  };

  const onClose = () => {
    sendEvent({ category: gaEventCategory, action: 'close' });
  };

  const onSendHandler = (commentText) => {
    if (editing) {
      editComment({ commentId, commentText, parentId });

      if (onSend) {
        onSend();
      }
      return;
    }

    const data = { aliasId, commentText, parentId };

    if (!isLoggedIn) {
      /**
       * Unfortunately for technical reasons we can't consider that the created
       * comment after login is visually considered a reply of a review.
       */
      createPendingComment({
        comment: data,
        domain,
        domainId,
        domainIdKey,
      });
      showDialog(LoginDialog);
      return;
    }

    createComment(data);

    if (onSend) {
      onSend();
    }
  };

  if (loadingLoggedInUser) {
    return null;
  }

  return (
    <div
      className={twMerge(
        'grid grid-cols-[auto_1fr] gap-2 md:gap-4',
        !editing && answering && 'pb-1 pr-2 pt-1 md:pl-10',
        className
      )}
    >
      {!editing && <UserAvatar />}
      <ShopperTextEditor
        answering={!editing ? answering : undefined}
        className={twJoin(
          'col-span-3',
          editing ? 'col-start-1' : 'col-start-2'
        )}
        gaEventCategory={gaEventCategory}
        initialValue={initialValue}
        placeholder={
          answering
            ? placeholder('inputs.placeholders.answering')
            : placeholder('inputs.placeholders.comment')
        }
        onCancel={onCancel}
        onClose={onClose}
        onFocus={onFocus}
        onSend={onSendHandler}
      >
        {children && (
          <div className="col-span-4 col-start-1 flex flex-col gap-y-4">
            {children}
          </div>
        )}
      </ShopperTextEditor>
    </div>
  );
};

TextEditor.propTypes = {
  aliasId: PropTypes.number,
  answering: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  commentId: PropTypes.number,
  editing: PropTypes.bool,
  initialValue: PropTypes.string,
  parentId: PropTypes.number,
  onCancel: PropTypes.func,
  onSend: PropTypes.func,
};

export default memo(TextEditor);
